import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Container } from 'semantic-ui-react'
import { useAuth } from '../context/AuthContext'
import { userApi } from '../misc/UserApi'
import { tripApi } from '../misc/TripApi'
import { reportApi } from '../misc/ReportApi'
import { reviewApi } from '../misc/ReviewApi'
import AdminTab from './AdminTab'
import { handleLogError } from '../misc/Helpers'

function AdminPage() {
  const Auth = useAuth()
  const user = Auth.getUser()
  const isAdmin = user.role === 'ADMIN'

  const [users, setUsers] = useState({totalPages: 0, content: []})
  const [userUsernameSearch, setUserUsernameSearch] = useState('')
  const [isUsersLoading, setIsUsersLoading] = useState(false)

  const [trips, setTrips] = useState({totalPages: 0, content: []})
  const [isTripsLoading, setIsTripsLoading] = useState(false)

  const [usertrips, setUsertrips] = useState({totalPages: 0, content: []})
  const [isUserTripsLoading, setIsUserTripsLoading] = useState(false)

  const [reports, setReports] = useState({totalPages: 0, content: []})
  const [isReportsLoading, setIsReportsLoading] = useState(false)

  const [reviews, setReviews] = useState({totalPages: 0, content: []})
  const [isReviewsLoading, setIsReviewsLoading] = useState(false)

  useEffect(() => {
    handleGetUsers()
    handleGetTrips()
    handleGetReports()
    handleGetReviews()
    handleGetUserTrips()
  }, [])

  const handleInputChange = (e, { name, value }) => {
    if (name === 'userUsernameSearch') {
      setUserUsernameSearch(value)
    }
  }

  const handleGetUsers = async (activePage) => {
    try {
      if(!activePage) {
        activePage = 1;
      }
      setIsUsersLoading(true)
      const response = await userApi.getUsersPage(user, activePage-1)
      const users = response.data
      setUsers(users)
    } catch (error) {
      handleLogError(error)
    } finally {
      setIsUsersLoading(false)
    }
  }

  const handleGetTrips = async (activePage) => {
    try {
      if(!activePage) {
        activePage = 1;
      }
      setIsTripsLoading(true)
      const response = await tripApi.getTripsPage(user, activePage-1)
      const trips = response.data
      setTrips(trips)
    } catch (error) {
      handleLogError(error)
    } finally {
      setIsTripsLoading(false)
    }
  }

  const handleGetUserTrips = async (activePage) => {
    try {
      if(!activePage) {
        activePage = 1;
      }
      setIsUserTripsLoading(true)
      const response = await tripApi.getUserTripsPage(user, activePage-1)
      const usertrips = response.data
      setUsertrips(usertrips)
    } catch (error) {
      handleLogError(error)
    } finally {
      setIsUserTripsLoading(false)
    }
  }

  const handleGetReports = async (activePage) => {
    try {
      if(!activePage) {
        activePage = 1;
      }
      setIsReportsLoading(true)
      const response = await reportApi.getReportsPage(user, activePage-1)
      const reports = response.data
      setReports(reports)
    } catch (error) {
      handleLogError(error)
    } finally {
      setIsReportsLoading(false)
    }
  }

  const handleGetReviews = async (activePage) => {
    try {
      if(!activePage) {
        activePage = 1;
      }
      setIsReviewsLoading(true)
      const response = await reviewApi.getReviewsPage(user, activePage-1)
      const reviews = response.data
      setReviews(reviews)
    } catch (error) {
      handleLogError(error)
    } finally {
      setIsReviewsLoading(false)
    }
  }

  const handlePaginationChange = async(e, { activePage }) => {
    handleGetUsers(activePage)
  }

  const handleTripPaginationChange = async(e, { activePage }) => {
    handleGetTrips(activePage)
  }

  const handleUserTripPaginationChange = async(e, { activePage }) => {
    handleGetUserTrips(activePage)
  }

  const handleReportPaginationChange = async(e, { activePage }) => {
    handleGetReports(activePage)
  }

  const handleReviewPaginationChange = async(e, { activePage }) => {
    handleGetReviews(activePage)
  }

  const handleDeleteUser = async (username) => {
    try {
      await userApi.deleteUser(user, username)
      await handleGetUsers()
    } catch (error) {
      handleLogError(error)
    }
  }

  const handleDeleteTrip = async (id) => {
    try {
      await tripApi.deleteTrip(user, id)
      await handleGetTrips()
    } catch (error) {
      handleLogError(error)
    }
  }

  const handleDeleteUserTrip = async (id) => {
    try {
      await tripApi.deleteUserTrip(user, id)
      await handleGetUserTrips()
    } catch (error) {
      handleLogError(error)
    }
  }

  const handleDeleteReport = async (id) => {
    try {
      await reportApi.deleteReport(user, id)
      await handleGetReports()
    } catch (error) {
      handleLogError(error)
    }
  }

  const handleDeleteReview = async (id) => {
    try {
      await reviewApi.deleteReview(user, id)
      await handleGetReviews()
    } catch (error) {
      handleLogError(error)
    }
  }

  const handleSearchUser = async () => {
    try {
      const response = await userApi.getUsers(user, userUsernameSearch)
      const data = response.data
      const users = data instanceof Array ? data : [data]
      setUsers(users)
    } catch (error) {
      handleLogError(error)
      setUsers([])
    }
  }

  if (!isAdmin) {
    return <Navigate to='/' />
  }

  return (
    <Container>
      <AdminTab
        isUsersLoading={isUsersLoading}
        users={users}
        userUsernameSearch={userUsernameSearch}
        handleDeleteUser={handleDeleteUser}
        handleSearchUser={handleSearchUser}
        handlePaginationChange={handlePaginationChange}
        isTripsLoading={isTripsLoading}
        trips={trips}
        handleDeleteTrip={handleDeleteTrip}
        handleTripPaginationChange={handleTripPaginationChange}
        isReportsLoading={isReportsLoading}
        reports={reports}
        handleDeleteReport={handleDeleteReport}
        handleReportPaginationChange={handleReportPaginationChange}
        isReviewsLoading={isReviewsLoading}
        reviews={reviews}
        handleDeleteReview={handleDeleteReview}
        handleReviewPaginationChange={handleReviewPaginationChange}
        isUserTripsLoading={isUserTripsLoading}
        usertrips={usertrips}
        handleDeleteUserTrip={handleDeleteUserTrip}
        handleUserTripPaginationChange={handleUserTripPaginationChange}
      />
    </Container>
  )
}

export default AdminPage
